// LOGOMAN PRO — generate fully-formed design candidates from the cross-product
// of (icons × layouts × palettes × font pairs), score each, return top N.
// This is what stitches every "section" of the editor together.

import { ICONS } from "../assets/icons";
import { scoreLogo, type AestheticScore } from "./aestheticScore";
import type { Layout, LogoState, CustomIcon, ContainerShape, IconPattern } from "../state/logoStore";
import type { ExtractedColor } from "./colorExtractor";

export interface DesignIdea {
  iconId: string;
  layout: Layout;
  titleFont: string;
  sloganFont: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
  background: string;
  titleSize: number;
  sloganSize: number;
  iconScale: number;
  containerShape: ContainerShape;
  containerColor: string;
  containerPattern: IconPattern;
  containerPatternColor: string;
  iconPattern: IconPattern;
  iconPatternColor: string;
  score: AestheticScore;
}

export interface GenerateOptions {
  /** Patient brand name + tagline so the score is computed against real text. */
  title: string;
  slogan: string;
  /** User-extracted palette from the Image tab (highest priority if present). */
  referencePalette?: ExtractedColor[];
  /** User custom icons to include in the icon mix. */
  customIcons?: CustomIcon[];
  /** Limit on returned ideas. */
  count?: number;
  /** Seed icon to bias around (e.g. user already picked one). null = explore widely. */
  preferIconId?: string | null;
  /** Reshuffle seed — different value gives different random container/pattern picks. */
  seed?: number;
}

// Curated icon picks — strong silhouettes across categories
const ICON_POOL = ["spark", "diamond", "hex", "drop", "shield", "leaf", "bolt", "infinity", "ring", "star"];

const LAYOUTS: Layout[] = ["icon-top", "icon-left", "icon-right", "text-only", "icon-only"];

// Hand-tuned font pairs — display + body that work together
const FONT_PAIRS: Array<{ title: string; slogan: string }> = [
  { title: "Montserrat",       slogan: "Inter" },
  { title: "Playfair Display", slogan: "Lora" },
  { title: "Bebas Neue",       slogan: "Inter" },
  { title: "Oswald",           slogan: "Inter" },
  { title: "Anton",            slogan: "Inter" },
  { title: "Poppins",          slogan: "Poppins" },
  { title: "Raleway",          slogan: "Raleway" },
  { title: "Merriweather",     slogan: "Lora" },
];

// Curated palettes (used when no reference image is provided AND mixed in always)
const CURATED_PALETTES: PaletteSet[] = [
  // Tech blue
  { background: "#ffffff", titleColor: "#0f172a", sloganColor: "#475569", iconColor: "#3b82f6" },
  // Inverted dark
  { background: "#0f172a", titleColor: "#fafafa", sloganColor: "#94a3b8", iconColor: "#facc15" },
  // Forest
  { background: "#f0fdf4", titleColor: "#14532d", sloganColor: "#65a30d", iconColor: "#16a34a" },
  // Warm
  { background: "#fff7ed", titleColor: "#7c2d12", sloganColor: "#c2410c", iconColor: "#ea580c" },
  // Mono
  { background: "#fafaf9", titleColor: "#0c0a09", sloganColor: "#78716c", iconColor: "#0c0a09" },
  // Royal
  { background: "#eef2ff", titleColor: "#1e1b4b", sloganColor: "#6366f1", iconColor: "#6366f1" },
  // Coast
  { background: "#f0f9ff", titleColor: "#0c4a6e", sloganColor: "#0284c7", iconColor: "#0ea5e9" },
  // Sunset
  { background: "#fdf2f8", titleColor: "#be185d", sloganColor: "#9333ea", iconColor: "#ec4899" },
];

interface PaletteSet {
  background: string; titleColor: string; sloganColor: string; iconColor: string;
}

// Container shapes weighted so half of ideas are plain (no container) — keeps the catalog balanced.
const CONTAINER_OPTIONS: Array<{ shape: ContainerShape; weight: number }> = [
  { shape: "none",    weight: 0.45 },
  { shape: "circle",  weight: 0.25 },
  { shape: "rounded", weight: 0.18 },
  { shape: "square",  weight: 0.12 },
];

// Pool of patterns used for both icon and container overlays
const PATTERN_POOL: IconPattern[] = [
  "stripes-h", "stripes-v", "stripes-diag", "stripes-thin", "stripes-thick",
  "stripes-double", "stripes-broken", "stripes-wavy",
  "dots", "dots-tiny", "dots-large", "dots-random", "bubble-dots", "halftone-fade",
  "grid", "grid-micro", "grid-blueprint", "checkerboard", "checkerboard-tiny",
  "honeycomb", "crosshatch", "carbon-fiber",
  "waves", "sound-waves", "heartbeat", "topographic",
  "circuit", "starburst", "concentric-rings", "bokeh", "confetti",
];

function seededRng(seed: number): () => number {
  let s = (seed | 0) || 1;
  return () => {
    s = (s * 1664525 + 1013904223) | 0;
    return ((s >>> 0) / 0xffffffff);
  };
}

function pickWeightedShape(r: () => number): ContainerShape {
  const total = CONTAINER_OPTIONS.reduce((a, b) => a + b.weight, 0);
  let pick = r() * total;
  for (const o of CONTAINER_OPTIONS) {
    pick -= o.weight;
    if (pick <= 0) return o.shape;
  }
  return "none";
}

function pickPattern(r: () => number): IconPattern {
  return PATTERN_POOL[Math.floor(r() * PATTERN_POOL.length)];
}

interface MixinChoice {
  containerShape: ContainerShape;
  iconPattern: IconPattern;
  containerPattern: IconPattern;
}

/**
 * Pick a container + pattern combination with sensible probabilities so we don't
 * end up with too many busy cards (icon + container patterns layered together).
 */
function rollMixin(r: () => number): MixinChoice {
  const shape = pickWeightedShape(r);
  const roll = r();
  let iconPattern: IconPattern = "none";
  let containerPattern: IconPattern = "none";
  if (shape === "none") {
    // No container — icon pattern only (or nothing)
    if (roll > 0.55) iconPattern = pickPattern(r);
  } else {
    if (roll < 0.40)      { /* clean container, no patterns */ }
    else if (roll < 0.70) { iconPattern = pickPattern(r); }
    else if (roll < 0.95) { containerPattern = pickPattern(r); }
    else                  { iconPattern = pickPattern(r); containerPattern = pickPattern(r); }
  }
  return { containerShape: shape, iconPattern, containerPattern };
}

/** Build a palette from an extracted image palette using lightness/saturation heuristics. */
function paletteFromExtracted(p: ExtractedColor[]): PaletteSet | null {
  if (p.length < 2) return null;
  const byLight = [...p].sort((a, b) => b.lightness - a.lightness);
  const bySat = [...p].sort((a, b) => b.saturation - a.saturation);
  return {
    background: byLight[0].hex,
    titleColor: byLight[byLight.length - 1].hex,
    sloganColor: byLight[Math.floor(byLight.length / 2)].hex,
    iconColor: bySat[0]?.hex || byLight[byLight.length - 1].hex,
  };
}

/** Build a dark-mode palette inversion from an extracted palette. */
function darkPaletteFromExtracted(p: ExtractedColor[]): PaletteSet | null {
  if (p.length < 2) return null;
  const byLight = [...p].sort((a, b) => a.lightness - b.lightness);
  const bySat = [...p].sort((a, b) => b.saturation - a.saturation);
  return {
    background: byLight[0].hex,
    titleColor: byLight[byLight.length - 1].hex,
    sloganColor: byLight[Math.floor(byLight.length / 2)].hex,
    iconColor: bySat[0]?.hex || byLight[byLight.length - 1].hex,
  };
}

/** Pick palette permutations from the palette (rotate which color plays which role). */
function paletteVariations(p: ExtractedColor[]): PaletteSet[] {
  const out: PaletteSet[] = [];
  const base = paletteFromExtracted(p);
  if (base) out.push(base);
  const dark = darkPaletteFromExtracted(p);
  if (dark) out.push(dark);
  // White-bg with each saturated color as the icon accent
  if (p.length >= 2) {
    const byLight = [...p].sort((a, b) => b.lightness - a.lightness);
    const sorted = [...p].sort((a, b) => b.saturation - a.saturation);
    for (let i = 0; i < Math.min(3, sorted.length); i++) {
      out.push({
        background: "#ffffff",
        titleColor: byLight[byLight.length - 1].hex,
        sloganColor: byLight[Math.floor(byLight.length / 2)].hex,
        iconColor: sorted[i].hex,
      });
    }
  }
  return out;
}

export function generateIdeas(opts: GenerateOptions): DesignIdea[] {
  const count = opts.count ?? 20;
  const r = seededRng(opts.seed ?? 0);

  // Build palette pool: extracted (high weight) + curated
  const palettes: PaletteSet[] = [
    ...(opts.referencePalette && opts.referencePalette.length > 0
      ? paletteVariations(opts.referencePalette)
      : []),
    ...CURATED_PALETTES,
  ];

  // Build icon pool: user's preferred + curated set + custom icons
  const iconIds = new Set<string>([
    ...(opts.preferIconId ? [opts.preferIconId] : []),
    ...ICON_POOL.filter((id) => ICONS.some((i) => i.id === id)),
    ...((opts.customIcons || []).slice(0, 4).map((c) => c.id)),
  ]);
  const iconList = Array.from(iconIds);

  const candidates: DesignIdea[] = [];
  for (const iconId of iconList) {
    for (const layout of LAYOUTS) {
      for (const palette of palettes) {
        // Two passes per (icon, layout, palette) so each cell can produce two
        // different mixin variants (e.g. plain + striped, circle + dotted).
        for (let pass = 0; pass < 2; pass++) {
          const pair = FONT_PAIRS[Math.abs(hashStr(iconId + layout + palette.background + pass)) % FONT_PAIRS.length];
          const mix = rollMixin(r);
          // Container color: prefer the icon accent so it harmonises; pattern color
          // tracks the background by default for an AT&T-style cutout.
          const containerColor = palette.iconColor;
          const containerPatternColor = palette.background;
          const iconPatternColor = palette.background;

          const idea: Omit<DesignIdea, "score"> = {
            iconId,
            layout,
            titleFont: pair.title,
            sloganFont: pair.slogan,
            titleColor: palette.titleColor,
            sloganColor: palette.sloganColor,
            iconColor: palette.iconColor,
            background: palette.background,
            titleSize: 56,
            sloganSize: 18,
            iconScale: 1,
            containerShape: mix.containerShape,
            containerColor,
            containerPattern: mix.containerPattern,
            containerPatternColor,
            iconPattern: mix.iconPattern,
            iconPatternColor,
          };
          const score = scoreLogo({
            state: {
              title: opts.title || "Your Brand",
              slogan: opts.slogan || "tagline",
              iconId, qrText: null,
              titleColor: palette.titleColor, sloganColor: palette.sloganColor,
              iconColor: palette.iconColor, background: palette.background,
              layout, titleSize: 56, sloganSize: 18, iconScale: 1,
              customIcons: opts.customIcons || [],
            },
          });
          candidates.push({ ...idea, score });
        }
      }
    }
  }

  // Rank, then de-duplicate similar designs. Include container + pattern in the
  // key so the same icon+palette can appear with a different visual treatment.
  candidates.sort((a, b) => b.score.score - a.score.score);
  const seen = new Set<string>();
  const out: DesignIdea[] = [];
  for (const c of candidates) {
    const key = `${c.iconId}-${c.background}-${c.iconColor}-${c.layout}-${c.containerShape}-${c.iconPattern}-${c.containerPattern}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
    if (out.length >= count) break;
  }
  return out;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
}

/** Apply a chosen idea to the logo store via setStyle/setIcon. */
export function ideaToStatePatch(idea: DesignIdea): Partial<LogoState> {
  return {
    iconId: idea.iconId,
    layout: idea.layout,
    titleFont: idea.titleFont,
    sloganFont: idea.sloganFont,
    titleColor: idea.titleColor,
    sloganColor: idea.sloganColor,
    iconColor: idea.iconColor,
    background: idea.background,
    titleSize: idea.titleSize,
    sloganSize: idea.sloganSize,
    iconScale: idea.iconScale,
    containerShape: idea.containerShape,
    containerColor: idea.containerColor,
    containerPattern: idea.containerPattern,
    containerPatternColor: idea.containerPatternColor,
    iconPattern: idea.iconPattern,
    iconPatternColor: idea.iconPatternColor,
  };
}
